import React from 'react';
import { useAttendanceGate } from '@/context/AttendanceContext';

/**
 * AttendanceGate - Enforcement-level-aware gate component (REBUILD-02/04)
 *
 * Behavior per enforcement level:
 *   - disabled:     Pass through, no UI
 *   - tracking:     Pass through, no UI
 *   - soft_warning: Dismissible amber banner above children, never blocks
 *   - hard_gate:    Blocks unrostered users (no approved roster) with FITTY warning
 *                   Blocks rostered users who haven't checked in
 *
 * Usage:
 *   <AttendanceGate blockType="page">
 *     <YourContent />
 *   </AttendanceGate>
 */
const AttendanceGate = ({ children, blockType = 'page' }) => {
  const { enforcementLevel, isCheckedIn, activeShift, showBanner, dismissBanner, rosterStatus, setShowCheckInModal } = useAttendanceGate();

  // Admin and Manager users bypass all attendance gates - they don't have rosters
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.role === 'admin' || user.role === 'manager') {
    return children;
  }

  // disabled or tracking: always pass through
  if (enforcementLevel === 'disabled' || enforcementLevel === 'tracking') {
    return children;
  }

  // soft_warning: show banner above content, never block
  if (enforcementLevel === 'soft_warning') {
    return (
      <>
        {showBanner && !isCheckedIn && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-amber-800">
                You have a shift scheduled. Please check in for attendance tracking.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCheckInModal(true)}
                className="px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm font-medium whitespace-nowrap"
              >
                Check In
              </button>
              <button
                onClick={dismissBanner}
                className="text-amber-600 hover:text-amber-800 text-sm font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {children}
      </>
    );
  }

  // hard_gate: block unrostered users (no approved roster for today)
  if (enforcementLevel === 'hard_gate' && !activeShift && rosterStatus !== 'approved') {
    return (
      <>
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6 m-4">
          <div className="text-center space-y-3">
            <div className="text-3xl">⚠️</div>
            <h2 className="text-xl font-bold text-red-800">No Active Roster</h2>
            <p className="text-red-700">
              You do not have an approved roster for today.
            </p>
            <p className="text-red-700 font-semibold">
              Contact your Manager immediately to get your roster approved by Admin (Chetan Sharma).
            </p>
            <p className="text-red-800 font-bold mt-4">
              Every minute you are not working is being marked and tracked.
            </p>
            <p className="text-red-600 font-semibold">
              This has been flagged to FITTY. All inactivity is being monitored in real-time.
            </p>
          </div>
        </div>
        {blockType === 'page' ? (
          <div className="opacity-30 pointer-events-none">{children}</div>
        ) : (
          children
        )}
      </>
    );
  }

  // hard_gate: block content if not checked in AND has active shift
  if (enforcementLevel === 'hard_gate' && activeShift && !isCheckedIn) {
    return (
      <>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-red-800 font-medium">
              Check-in required. Please check in at your assigned location before submitting data.
            </span>
            <button
              onClick={() => setShowCheckInModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2 whitespace-nowrap"
            >
              Check In Now
            </button>
          </div>
        </div>
        {blockType === 'page' ? (
          <div className="opacity-50 pointer-events-none">{children}</div>
        ) : (
          children
        )}
      </>
    );
  }

  // Default: pass through (no shift, or checked in, or enforcement not active)
  return children;
};

export default AttendanceGate;
